import { canvasToBlob, decodeArtworkBlob } from "../persistence/artworkCodec";
import type { Dimensions } from "../canvas/canvasTypes";
import type { StoredRasterLayerBlob } from "./layerTypes";

export interface EncodedRasterLayer extends StoredRasterLayerBlob {
  generation: number;
  encoded: boolean;
}

/** A document-sized canvas is allocated only when the layer receives pixels. */
export class RasterLayerSurface {
  private canvas: HTMLCanvasElement | null = null;
  private committedBlob: Blob | undefined;
  private dirtyGeneration = 0;

  public constructor(
    public readonly layerId: string,
    private readonly size: Dimensions,
  ) {}

  public getCanvas(): HTMLCanvasElement | null {
    return this.canvas;
  }

  public isAllocated(): boolean {
    return this.canvas !== null;
  }

  public ensureContext(): CanvasRenderingContext2D {
    if (!this.canvas) {
      this.canvas = document.createElement("canvas");
      this.canvas.width = this.size.width;
      this.canvas.height = this.size.height;
    }
    const context = this.canvas.getContext("2d");
    if (!context) throw new Error("Canvas 2D is unavailable for this layer.");
    return context;
  }

  public markChanged(): void {
    this.dirtyGeneration += 1;
    this.committedBlob = undefined;
  }

  public clear(): void {
    if (this.canvas) {
      this.ensureContext().clearRect(0, 0, this.size.width, this.size.height);
      this.canvas.width = 1;
      this.canvas.height = 1;
    }
    this.canvas = null;
    this.markChanged();
  }

  public async load(record: StoredRasterLayerBlob): Promise<void> {
    if (record.layerId !== this.layerId) throw new Error("Raster layer ID does not match.");
    if (record.blank) {
      this.canvas = null;
      this.committedBlob = undefined;
      this.dirtyGeneration = 0;
      return;
    }
    if (!record.blob || record.blob.type !== "image/png" || record.blob.size === 0) {
      throw new Error("Raster layer PNG is missing.");
    }
    const decoded = await decodeArtworkBlob(record.blob, this.size);
    try {
      const context = this.ensureContext();
      context.clearRect(0, 0, this.size.width, this.size.height);
      context.drawImage(decoded.source, 0, 0);
      this.committedBlob = record.blob;
      this.dirtyGeneration = 0;
    } finally {
      decoded.release();
    }
  }

  public clone(): RasterLayerSurface {
    const copy = new RasterLayerSurface(this.layerId, this.size);
    if (this.canvas) copy.ensureContext().drawImage(this.canvas, 0, 0);
    copy.committedBlob = this.committedBlob;
    copy.dirtyGeneration = this.dirtyGeneration;
    return copy;
  }

  public async encode(): Promise<EncodedRasterLayer> {
    if (!this.canvas) {
      return {
        layerId: this.layerId,
        mimeType: "image/png",
        blank: true,
        generation: this.dirtyGeneration,
        encoded: false,
      };
    }
    if (this.committedBlob) {
      return {
        layerId: this.layerId,
        mimeType: "image/png",
        blank: false,
        blob: this.committedBlob,
        generation: this.dirtyGeneration,
        encoded: false,
      };
    }
    return {
      layerId: this.layerId,
      mimeType: "image/png",
      blank: false,
      blob: await canvasToBlob(this.canvas, "image/png"),
      generation: this.dirtyGeneration,
      encoded: true,
    };
  }

  public acknowledge(record: EncodedRasterLayer): void {
    if (record.layerId !== this.layerId || record.generation !== this.dirtyGeneration) return;
    this.committedBlob = record.blank ? undefined : record.blob;
  }

  public destroy(): void {
    if (this.canvas) {
      this.canvas.width = 1;
      this.canvas.height = 1;
      this.canvas = null;
    }
    this.committedBlob = undefined;
  }
}
